import { useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from "react";
import { Avatar, Button } from "@pack-it-up/ui-web";
import type { Assignment, RelativeOffset } from "@pack-it-up/core";
import type { ParticipantId } from "@pack-it-up/shared";
import type { Person } from "./people.js";
import { TagField } from "./TagField.js";
import { RelativeDueControl } from "../shared/RelativeDueControl.js";
import { TrashIcon } from "../../app/icons.js";
import * as styles from "./ItemComposer.css.js";

// The shared composer body (gap-analysis §6/§6a, T-G): one component used by both the
// add-item flow and RowEditForm, differing only in initial values, the primary-button
// label, and what the caller does with the committed draft. Layout follows the kit
// `.item-composer` exactly: dashed placeholder checkbox + borderless name row, an
// indented controls row (qty/due-date stepper, FOR segment, ASSIGN avatars), a notes
// textarea, and right-aligned actions.

export type ForKind = Assignment["kind"];

export interface ItemComposerDraft {
  name: string;
  quantity: number;
  assignment: Assignment;
  notes: string;
  dueDate: string;
  tags: string[];
  // Issue #188 — the "relative" due mode's value (a template's to-dos are due N days
  // before/after the trip starts, Doc 07 §6). `null` clears it; absent when the
  // composer is in its ordinary calendar-date mode.
  relativeDueOffset?: RelativeOffset | null;
}

export interface ItemComposerInitial {
  name: string;
  quantity?: number; // packing variant only
  assignment: Assignment;
  notes: string;
  dueDate?: string; // todo variant only
  relativeDueOffset?: RelativeOffset; // "relative" due mode only (issue #188)
  tags?: string[];
}

const FormShell = ({
  onCommit,
  onCancel,
  blurBehavior = "commit",
  children,
}: {
  onCommit: () => void;
  onCancel: () => void;
  // What focus-leaving-the-card does: "commit" (default, used by RowEditForm) applies the
  // draft like Enter/Save would; "discard" cancels the draft instead, like Esc/Cancel
  // (restores the original add-flow semantics — typing a name then clicking away discards).
  blurBehavior?: "commit" | "discard";
  children: ReactNode;
}): ReactNode => {
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      onCommit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };
  // `relatedTarget` (the element gaining focus) is the primary signal that a blur is a
  // real focus-leave vs. a click within the card. But `<button>` elements don't reliably
  // receive focus on mouse click in every browser (notably Safari/Firefox on macOS), so
  // `relatedTarget` can land on `null`/body even when the user clicked the composer's own
  // Add/Cancel button. Track mousedown-within-the-card as a fallback: if the mousedown
  // that's about to cause this blur originated inside the card, treat it as internal and
  // let the button's own onClick fire normally instead of committing/discarding early.
  const mouseDownInsideRef = useRef(false);
  const onMouseDown = (): void => {
    // This handler only fires for mousedowns inside the card. Any blur caused by this
    // mousedown fires synchronously (before the timeout), so it sees the flag; the
    // timeout then clears it so a later mousedown OUTSIDE the card (which never reaches
    // this handler) can't read a stale `true` and have its focus-leave swallowed.
    mouseDownInsideRef.current = true;
    window.setTimeout(() => {
      mouseDownInsideRef.current = false;
    }, 0);
  };
  const onBlur = (e: FocusEvent<HTMLDivElement>): void => {
    const relatedInside = e.currentTarget.contains(e.relatedTarget as Node | null);
    if (relatedInside || mouseDownInsideRef.current) return;
    if (blurBehavior === "discard") onCancel();
    else onCommit();
  };
  return (
    <div onKeyDown={onKeyDown} onMouseDown={onMouseDown} onBlur={onBlur} style={{ width: "100%" }}>
      {children}
    </div>
  );
};

const QtyStepper = ({ value, onChange }: { value: number; onChange: (n: number) => void }): ReactNode => (
  <div className={styles.stepper}>
    <button
      type="button"
      aria-label="Decrease quantity"
      className={styles.step}
      onClick={() => onChange(Math.max(1, value - 1))}
    >
      −
    </button>
    <span className={styles.qty} aria-label="Quantity">
      {value}
    </span>
    <button type="button" aria-label="Increase quantity" className={styles.step} onClick={() => onChange(value + 1)}>
      +
    </button>
  </div>
);

const allocationFor = (a: Assignment, pid: ParticipantId): { quantity: number } | undefined =>
  a.kind === "specific" ? a.allocations.find((x) => x.participantId === pid) : undefined;

// FOR segmented control (Everyone / Individuals / Shared) — kit `.ic-seg`. "Individuals"
// maps to the `specific` assignment kind (gap-analysis §6a).
const ForSegment = ({ kind, onChange }: { kind: ForKind; onChange: (kind: ForKind) => void }): ReactNode => (
  <div className={styles.seg} role="group" aria-label="Assign to">
    <button
      type="button"
      className={[styles.segBtn, kind === "everyone" ? styles.segBtnOn.blue : ""].join(" ")}
      aria-pressed={kind === "everyone"}
      onClick={() => onChange("everyone")}
    >
      Everyone
    </button>
    <button
      type="button"
      className={[styles.segBtn, kind === "specific" ? styles.segBtnOn.blue : ""].join(" ")}
      aria-pressed={kind === "specific"}
      onClick={() => onChange("specific")}
    >
      Individuals
    </button>
    <button
      type="button"
      className={[styles.segBtn, kind === "shared" ? styles.segBtnOn.amber : ""].join(" ")}
      aria-pressed={kind === "shared"}
      onClick={() => onChange("shared")}
    >
      Shared
    </button>
  </div>
);

const AssignRow = ({
  assignment,
  people,
  onToggle,
  onQuantityChange,
}: {
  assignment: Assignment;
  people: Person[];
  onToggle: (pid: ParticipantId) => void;
  onQuantityChange: (pid: ParticipantId, quantity: number) => void;
}): ReactNode => {
  return (
    <div className={styles.assignees}>
      {people.map(({ id: pid, displayName: name }) => {
        const allocation = allocationFor(assignment, pid);
        const avatarButton = (
          <button
            type="button"
            className={[styles.av, allocation !== undefined ? styles.avSelected : ""].join(" ")}
            aria-pressed={allocation !== undefined}
            aria-label={`Assign ${name}`}
            title={name}
            onClick={() => onToggle(pid)}
          >
            <Avatar name={name} size="sm" />
          </button>
        );
        if (allocation === undefined) return <span key={pid}>{avatarButton}</span>;
        // Selected participant: the avatar grows a qty stepper "tail" out its right side,
        // both wrapped in one continuous pill (per-person qty, issue: individuals qty).
        const { quantity } = allocation;
        return (
          <span key={pid} className={styles.pill}>
            {avatarButton}
            <button
              type="button"
              className={styles.pillStep}
              aria-label={`Decrease ${name}'s quantity`}
              onClick={() => onQuantityChange(pid, quantity - 1)}
            >
              −
            </button>
            <span className={styles.pillQty} aria-label={`${name}'s quantity`}>
              {quantity}
            </span>
            <button
              type="button"
              className={styles.pillStep}
              aria-label={`Increase ${name}'s quantity`}
              onClick={() => onQuantityChange(pid, quantity + 1)}
            >
              +
            </button>
          </span>
        );
      })}
    </div>
  );
};

export interface ItemComposerProps {
  ariaLabel: string;
  initial: ItemComposerInitial;
  // Who this list's rows can be assigned to: a trip's members, or a template's
  // participants (issue #185).
  people: Person[];
  // Tags already in use on this trip/template (T29) — offered as one-click chips.
  // Derived from the rows themselves; there is no tag registry.
  tagPalette?: string[];
  primaryLabel: "Add item" | "Save";
  variant?: "item" | "todo"; // "todo" swaps the qty stepper for a due-date field
  // What the "todo" variant's due field is: a calendar date (a trip) or an offset from
  // the trip's start date (a template — issue #188).
  dueMode?: "date" | "relative";
  inset?: boolean; // add-flow gets the kit's inset margins; RowEditForm omits them
  // What focus-leaving-the-card does: "commit" (default) applies the draft like
  // Enter/Save; "discard" cancels it like Esc/Cancel. RowEditForm omits this (keeps
  // commit-on-blur); the add flow passes "discard" to restore the original add-flow
  // semantics — typing a name and clicking away discards, Enter/Add commits.
  blurBehavior?: "commit" | "discard";
  onCommit: (draft: ItemComposerDraft) => void;
  onCancel: () => void;
  onResetToTemplate?: () => void;
  // Present only in edit mode (RowEditForm) — the add flow has no existing row to
  // delete. No confirm here — the caller defers the actual delete behind an Undo
  // toast instead (see TripDetailPage/TemplateEditor), so a stray click is cheap to
  // reverse without an extra dialog in the way.
  onDelete?: () => void;
}

export const ItemComposer = ({
  ariaLabel,
  initial,
  people,
  tagPalette = [],
  primaryLabel,
  variant = "item",
  dueMode = "date",
  inset = false,
  blurBehavior = "commit",
  onCommit,
  onCancel,
  onResetToTemplate,
  onDelete,
}: ItemComposerProps): ReactNode => {
  const [name, setName] = useState(initial.name);
  const [quantity, setQuantity] = useState(initial.quantity ?? 1);
  const [assignment, setAssignment] = useState<Assignment>(initial.assignment);
  const [notes, setNotes] = useState(initial.notes);
  const [dueDate, setDueDate] = useState(initial.dueDate ?? "");
  const [relativeDue, setRelativeDue] = useState<RelativeOffset | undefined>(initial.relativeDueOffset);
  const [tags, setTags] = useState<string[]>(initial.tags ?? []);

  const commit = (): void => {
    onCommit({
      name,
      quantity,
      assignment,
      notes,
      dueDate,
      tags,
      ...(dueMode === "relative" ? { relativeDueOffset: relativeDue ?? null } : {}),
    });
  };

  const pickKind = (kind: ForKind): void => {
    if (kind === "everyone") setAssignment({ kind: "everyone" });
    else if (kind === "shared") setAssignment({ kind: "shared" });
    // Issue #245 — "Individuals" starts with no one picked; the ASSIGN row is where you
    // choose. (It used to preselect everyone, which made "Individuals" mean "everyone"
    // until you deselected people.)
    else setAssignment({ kind: "specific", allocations: [] });
  };

  const toggleParticipant = (pid: ParticipantId): void => {
    setAssignment((a) => {
      if (a.kind !== "specific") return a;
      const has = a.allocations.some((x) => x.participantId === pid);
      const allocations = has
        ? a.allocations.filter((x) => x.participantId !== pid)
        : [...a.allocations, { participantId: pid, quantity: 1 }];
      return { kind: "specific", allocations };
    });
  };

  // Issue #245 — stepping a person's quantity below 1 unassigns them, so "−" all the way
  // down is the same gesture as tapping their avatar off.
  const setAllocationQuantity = (pid: ParticipantId, quantity: number): void => {
    setAssignment((a) => {
      if (a.kind !== "specific") return a;
      const allocations = quantity < 1
        ? a.allocations.filter((x) => x.participantId !== pid)
        : a.allocations.map((x) => (x.participantId === pid ? { ...x, quantity } : x));
      return { kind: "specific", allocations };
    });
  };

  const resetButton = onResetToTemplate !== undefined ? (
    <button type="button" onClick={onResetToTemplate} className={styles.resetLink}>
      Reset to template
    </button>
  ) : undefined;

  return (
    <FormShell onCommit={commit} onCancel={onCancel} blurBehavior={blurBehavior}>
      <div className={[styles.panel, inset ? styles.panelInset : ""].join(" ")}>
        <div className={styles.row1}>
          <div className={styles.dashedCheck} aria-hidden />
          <input
            aria-label={ariaLabel}
            className={styles.nameInput}
            placeholder="Item name…"
            value={name}
            autoFocus
            onFocus={(e) => e.target.select()}
            onChange={(e) => setName(e.target.value)}
          />
          {onDelete !== undefined && (
            <button
              type="button"
              className={styles.deleteBtn}
              aria-label={`Delete ${initial.name}`}
              title="Delete"
              onClick={onDelete}
            >
              {TrashIcon}
            </button>
          )}
        </div>

        <div className={styles.controls}>
          <div className={styles.field}>
            <span className={styles.flabel}>{variant === "todo" ? "Due" : "Qty"}</span>
            {variant === "todo" && dueMode === "relative" ? (
              <RelativeDueControl value={relativeDue} onChange={setRelativeDue} />
            ) : variant === "todo" ? (
              <input
                aria-label="Due date"
                type="date"
                className={styles.dueDateInput}
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            ) : (
              <QtyStepper value={quantity} onChange={setQuantity} />
            )}
          </div>

          <div className={styles.field}>
            <span className={styles.flabel}>For</span>
            <ForSegment kind={assignment.kind} onChange={pickKind} />
          </div>

          {assignment.kind === "specific" && (
            <div className={styles.field}>
              <span className={styles.flabel}>Assign</span>
              <AssignRow
                assignment={assignment}
                people={people}
                onToggle={toggleParticipant}
                onQuantityChange={setAllocationQuantity}
              />
            </div>
          )}
        </div>

        <textarea
          aria-label="Description"
          placeholder="Add a note or description (optional)…"
          className={styles.notes}
          rows={1}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className={styles.actions}>
          <div className={styles.actionsLeft}>
            <span className={styles.flabel}>Tags</span>
            <TagField value={tags} palette={tagPalette} onChange={setTags} />
            {resetButton}
          </div>
          <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="button" onClick={commit}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </FormShell>
  );
};
